'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { crmService } from '@/lib/services/crmService';

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  jobTitle: string;
  companySize: string;
  industry: string;
  aiInitiative: string;
  timeline: string;
  challenges: string;
  preferredDate: string;
  preferredTime: string;
  gdprConsent: boolean;
}

interface FormErrors {
  [key: string]: string;
}

const DiscoveryCallForm = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    jobTitle: '',
    companySize: '',
    industry: '',
    aiInitiative: '',
    timeline: '',
    challenges: '',
    preferredDate: '',
    preferredTime: '',
    gdprConsent: false
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const companySizes = [
    '1-50 employees',
    '51-200 employees',
    '201-500 employees',
    '501-1,000 employees',
    '1,001-5,000 employees',
    '5,001+ employees'
  ];

  const industries = [
    'Financial Services',
    'Healthcare',
    'Manufacturing',
    'Retail & E-commerce',
    'Technology',
    'Professional Services',
    'Education',
    'Government',
    'Other'
  ];

  const timelines = [
    'Immediate (within 1 month)',
    'Short-term (1-3 months)',
    'Medium-term (3-6 months)',
    'Long-term (6+ months)',
    'Exploring options'
  ];

  const timeSlots = [
    '09:00 - 10:00',
    '10:00 - 11:00',
    '11:00 - 12:00',
    '12:00 - 13:00',
    '13:00 - 14:00',
    '14:00 - 15:00',
    '15:00 - 16:00',
    '16:00 - 17:00'
  ];

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    const phoneRegex = /^[\d\s\+\-\(\)]+$/;
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    }

    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = 'Job title is required';
    }

    if (!formData.companySize) {
      newErrors.companySize = 'Please select company size';
    }

    if (!formData.industry) {
      newErrors.industry = 'Please select industry';
    }

    if (!formData.aiInitiative.trim()) {
      newErrors.aiInitiative = 'Please describe your AI initiative';
    }

    if (!formData.timeline) {
      newErrors.timeline = 'Please select timeline';
    }

    if (!formData.preferredDate) {
      newErrors.preferredDate = 'Please select preferred date';
    }

    if (!formData.preferredTime) {
      newErrors.preferredTime = 'Please select preferred time';
    }

    if (!formData.gdprConsent) {
      newErrors.gdprConsent = 'You must consent to data processing';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await crmService.submitDiscoveryCall({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        jobTitle: formData.jobTitle,
        companySize: formData.companySize,
        industry: formData.industry,
        aiInitiative: formData.aiInitiative,
        timeline: formData.timeline,
        challenges: formData.challenges,
        preferredDate: formData.preferredDate,
        preferredTime: formData.preferredTime,
      });

      setIsSubmitting(false);
      setSubmitSuccess(true);

      setFormData({
        fullName: '',
        email: '',
        phone: '',
        company: '',
        jobTitle: '',
        companySize: '',
        industry: '',
        aiInitiative: '',
        timeline: '',
        challenges: '',
        preferredDate: '',
        preferredTime: '',
        gdprConsent: false
      });

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error: any) {
      setIsSubmitting(false);
      setSubmitError(error?.message || 'Failed to submit discovery call request. Please try again.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg shadow-card p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-32 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (submitSuccess) {
    return (
      <div className="bg-card rounded-lg shadow-card p-8 lg:p-12">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mb-6">
            <Icon name="CheckCircleIcon" size={40} variant="solid" className="text-success" />
          </div>
          <h3 className="text-2xl font-bold font-headline text-primary mb-4">
            Discovery Call Booked Successfully!
          </h3>
          <p className="text-lg text-muted-foreground font-body mb-6">
            Thank you for your interest in AI Change Leaders. We've received your discovery call request and will send you a confirmation email shortly.
          </p>
          <div className="bg-primary/5 rounded-lg p-6 mb-8">
            <h4 className="font-semibold font-headline text-primary mb-3">What happens next?</h4>
            <ul className="space-y-2 text-left">
              <li className="flex items-start gap-2">
                <Icon name="EnvelopeIcon" size={20} variant="outline" className="text-secondary mt-0.5" />
                <span className="text-sm text-foreground font-body">Confirmation email sent to your inbox</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="CalendarIcon" size={20} variant="outline" className="text-secondary mt-0.5" />
                <span className="text-sm text-foreground font-body">Calendar invite with meeting details</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="DocumentTextIcon" size={20} variant="outline" className="text-secondary mt-0.5" />
                <span className="text-sm text-foreground font-body">Pre-call questionnaire to maximize our time</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="UserIcon" size={20} variant="outline" className="text-secondary mt-0.5" />
                <span className="text-sm text-foreground font-body">Assigned transformation specialist introduction</span>
              </li>
            </ul>
          </div>
          <button
            onClick={() => setSubmitSuccess(false)}
            className="px-6 py-3 rounded-md font-semibold font-cta text-sm text-secondary-foreground bg-secondary hover:bg-secondary/90 transition-all duration-300"
          >
            Book Another Call
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-card p-8 lg:p-12">
      <div className="mb-8">
        <h3 className="text-2xl font-bold font-headline text-primary mb-3">
          Book Your Discovery Call
        </h3>
        <p className="text-muted-foreground font-body">
          Complete the form below to schedule a 30-minute consultation with our AI transformation specialists. We'll discuss your challenges, goals, and how we can support your journey.
        </p>
      </div>

      {submitError && (
        <div className="mb-6 p-4 rounded-md bg-error/10 border border-error/20">
          <div className="flex items-start gap-3">
            <Icon name="ExclamationTriangleIcon" size={20} variant="solid" className="text-error mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-error font-body mb-1">Submission Failed</p>
              <p className="text-sm text-error/80 font-body">{submitError}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="fullName" className="block text-sm font-semibold font-body text-foreground mb-2">
              Full Name <span className="text-error">*</span>
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-md border ${
                errors.fullName ? 'border-error' : 'border-input'
              } bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300`}
              placeholder="John Smith"
            />
            {errors.fullName && (
              <p className="mt-1 text-sm text-error font-body">{errors.fullName}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-semibold font-body text-foreground mb-2">
              Email Address <span className="text-error">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-md border ${
                errors.email ? 'border-error' : 'border-input'
              } bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300`}
              placeholder="john.smith@company.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-error font-body">{errors.email}</p>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="phone" className="block text-sm font-semibold font-body text-foreground mb-2">
              Phone Number <span className="text-error">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-md border ${
                errors.phone ? 'border-error' : 'border-input'
              } bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300`}
              placeholder="+44 20 1234 5678"
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-error font-body">{errors.phone}</p>
            )}
          </div>

          <div>
            <label htmlFor="company" className="block text-sm font-semibold font-body text-foreground mb-2">
              Company Name <span className="text-error">*</span>
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-md border ${
                errors.company ? 'border-error' : 'border-input'
              } bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300`}
              placeholder="Acme Corporation"
            />
            {errors.company && (
              <p className="mt-1 text-sm text-error font-body">{errors.company}</p>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="jobTitle" className="block text-sm font-semibold font-body text-foreground mb-2">
              Job Title <span className="text-error">*</span>
            </label>
            <input
              type="text"
              id="jobTitle"
              name="jobTitle"
              value={formData.jobTitle}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-md border ${
                errors.jobTitle ? 'border-error' : 'border-input'
              } bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300`}
              placeholder="VP of Digital Transformation"
            />
            {errors.jobTitle && (
              <p className="mt-1 text-sm text-error font-body">{errors.jobTitle}</p>
            )}
          </div>

          <div>
            <label htmlFor="companySize" className="block text-sm font-semibold font-body text-foreground mb-2">
              Company Size <span className="text-error">*</span>
            </label>
            <select
              id="companySize"
              name="companySize"
              value={formData.companySize}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-md border ${
                errors.companySize ? 'border-error' : 'border-input'
              } bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300`}
            >
              <option value="">Select company size</option>
              {companySizes.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            {errors.companySize && (
              <p className="mt-1 text-sm text-error font-body">{errors.companySize}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="industry" className="block text-sm font-semibold font-body text-foreground mb-2">
            Industry <span className="text-error">*</span>
          </label>
          <select
            id="industry"
            name="industry"
            value={formData.industry}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-md border ${
              errors.industry ? 'border-error' : 'border-input'
            } bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300`}
          >
            <option value="">Select industry</option>
            {industries.map((industry) => (
              <option key={industry} value={industry}>{industry}</option>
            ))}
          </select>
          {errors.industry && (
            <p className="mt-1 text-sm text-error font-body">{errors.industry}</p>
          )}
        </div>

        <div>
          <label htmlFor="aiInitiative" className="block text-sm font-semibold font-body text-foreground mb-2">
            Describe Your AI Initiative <span className="text-error">*</span>
          </label>
          <textarea
            id="aiInitiative"
            name="aiInitiative"
            value={formData.aiInitiative}
            onChange={handleChange}
            rows={4}
            className={`w-full px-4 py-3 rounded-md border ${
              errors.aiInitiative ? 'border-error' : 'border-input'
            } bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300 resize-none`}
            placeholder="Tell us about your AI transformation goals, current challenges, and what you hope to achieve..."
          />
          {errors.aiInitiative && (
            <p className="mt-1 text-sm text-error font-body">{errors.aiInitiative}</p>
          )}
        </div>

        <div>
          <label htmlFor="timeline" className="block text-sm font-semibold font-body text-foreground mb-2">
            Implementation Timeline <span className="text-error">*</span>
          </label>
          <select
            id="timeline"
            name="timeline"
            value={formData.timeline}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-md border ${
              errors.timeline ? 'border-error' : 'border-input'
            } bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300`}
          >
            <option value="">Select timeline</option>
            {timelines.map((timeline) => (
              <option key={timeline} value={timeline}>{timeline}</option>
            ))}
          </select>
          {errors.timeline && (
            <p className="mt-1 text-sm text-error font-body">{errors.timeline}</p>
          )}
        </div>

        <div>
          <label htmlFor="challenges" className="block text-sm font-semibold font-body text-foreground mb-2">
            Key Challenges (Optional)
          </label>
          <textarea
            id="challenges"
            name="challenges"
            value={formData.challenges}
            onChange={handleChange}
            rows={3}
            className="w-full px-4 py-3 rounded-md border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300 resize-none"
            placeholder="What specific challenges are you facing with AI adoption?"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="preferredDate" className="block text-sm font-semibold font-body text-foreground mb-2">
              Preferred Date <span className="text-error">*</span>
            </label>
            <input
              type="date"
              id="preferredDate"
              name="preferredDate"
              value={formData.preferredDate}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
              className={`w-full px-4 py-3 rounded-md border ${
                errors.preferredDate ? 'border-error' : 'border-input'
              } bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300`}
            />
            {errors.preferredDate && (
              <p className="mt-1 text-sm text-error font-body">{errors.preferredDate}</p>
            )}
          </div>

          <div>
            <label htmlFor="preferredTime" className="block text-sm font-semibold font-body text-foreground mb-2">
              Preferred Time (UK Time) <span className="text-error">*</span>
            </label>
            <select
              id="preferredTime"
              name="preferredTime"
              value={formData.preferredTime}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-md border ${
                errors.preferredTime ? 'border-error' : 'border-input'
              } bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300`}
            >
              <option value="">Select time slot</option>
              {timeSlots.map((slot) => (
                <option key={slot} value={slot}>{slot}</option>
              ))}
            </select>
            {errors.preferredTime && (
              <p className="mt-1 text-sm text-error font-body">{errors.preferredTime}</p>
            )}
          </div>
        </div>

        <div className="bg-muted/50 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="gdprConsent"
              name="gdprConsent"
              checked={formData.gdprConsent}
              onChange={handleChange}
              className="mt-1 w-5 h-5 rounded border-input text-secondary focus:ring-2 focus:ring-ring"
            />
            <label htmlFor="gdprConsent" className="text-sm text-foreground font-body">
              I consent to AI Change Leaders processing my personal data in accordance with the GDPR. I understand my data will be used to schedule and prepare for our discovery call, and I can withdraw consent at any time. <span className="text-error">*</span>
            </label>
          </div>
          {errors.gdprConsent && (
            <p className="mt-2 text-sm text-error font-body ml-8">{errors.gdprConsent}</p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 px-8 py-4 rounded-md font-semibold font-cta text-base text-secondary-foreground bg-secondary hover:bg-secondary/90 disabled:bg-muted disabled:text-muted-foreground transition-all duration-300 shadow-subtle hover:shadow-strong hover:-translate-y-0.5 disabled:hover:translate-y-0 disabled:hover:shadow-subtle"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
                Booking Call...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Icon name="CalendarIcon" size={20} variant="outline" />
                Book Discovery Call
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DiscoveryCallForm;